import React, { useEffect, useReducer, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Avatar,
    Button,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';
import { Form, InputGroup } from 'react-bootstrap';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import SearchIcon from '@material-ui/icons/Search';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import StoreOutlinedIcon from '@material-ui/icons/StoreOutlined';
import DirectionsCarOutlinedIcon from '@material-ui/icons/DirectionsCarOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import PaymentOutlinedIcon from '@material-ui/icons/PaymentOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import AttachMoneyOutlinedIcon from '@material-ui/icons/AttachMoneyOutlined';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import CreditCardOutlinedIcon from '@material-ui/icons/CreditCardOutlined';
import BarChartOutlinedIcon from '@material-ui/icons/BarChartOutlined';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import SchoolOutlinedIcon from '@material-ui/icons/SchoolOutlined';
import RoomServiceOutlinedIcon from '@material-ui/icons/RoomServiceOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import update from 'immutability-helper';
import clsx from 'clsx';

import Select from 'components/widgets/Select';
import Loading from 'components/widgets/Loading';
import FormattedTooltip from 'components/widgets/FormattedTooltip';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { FetchPolicy } from 'utils/enum/Core';
import { modules, HR_SUBMODULES } from 'utils/enum/modules';
import EmployeeQuery from 'services/graphQL/query/hr/EmployeeQuery';
import EmployeeMutation from 'services/graphQL/mutate/hr/EmployeeMutation';
import {
    DASHBOARD_VIEW_OPTIONS,
    PERMISSION_META_KEYS,
    getAllCatalogKeys,
    getPermissionModules,
    getPermissionValue,
} from 'utils/enum/EmployeePermissionCatalog';
import LoginAndPermissionDetailStyles from 'styles/modules/hr/LoginAndPermissionDetailStyles';
import avatarUser from 'assets/user/userDefault.jpg';

const useStyles = makeStyles((theme) => LoginAndPermissionDetailStyles.page(theme));

const PASSWORD_SPECIAL_CHARS = [
    '!', '%', '*', '_', '+', '~', '-', '[', ']', '{', '}', '#', '=', '$', '@',
    '<', '>', ',', '.', ';', '?', "'", '"', '&', '(', ')', '^', '\\', '|', '/',
];
const PASSWORD_SPECIAL_LABEL = '!%*_+~-[]{}#=$@<>,.;?\'"&()^\\|/';

const PASSWORD_RULES = [
    {
        key: 'length',
        label: '8-50 characters.',
    },
    {
        key: 'number',
        label: 'At least one number.',
    },
    {
        key: 'capitalLetter',
        label: 'At least one capital letter.',
    },
    {
        key: 'lowercaseLetter',
        label: 'At least one lowercase letter.',
    },
    {
        key: 'specialCharacter',
        label: `At least one of these special character: ${PASSWORD_SPECIAL_LABEL}`,
    },
    {
        key: 'personalInformation',
        label: 'Name, Birthday, Email, Work Email and Nationality are not allowed.',
    },
];

const getPersonalInfoTokens = (employee = {}, userName = '') => {
    const tokens = [];
    const fullName = String(employee?.fullName || '').trim();
    if (fullName) {
        tokens.push(fullName);
        fullName.split(/\s+/).forEach((part) => {
            if (part.length > 1) tokens.push(part);
        });
    }
    if (employee?.workEmail) tokens.push(String(employee.workEmail).trim());
    if (userName) tokens.push(String(userName).trim());
    return tokens.filter(Boolean);
};

const containsPersonalInformation = (password = '', tokens = []) => {
    const value = String(password || '').toLowerCase();
    if (!value) return false;
    return tokens.some((token) => {
        const normalized = String(token || '').trim().toLowerCase();
        return normalized.length > 1 && value.includes(normalized);
    });
};

const hasSpecialCharacter = (password = '') => (
    PASSWORD_SPECIAL_CHARS.some((char) => password.includes(char))
);

const validateLoginPassword = (password = '', personalTokens = []) => {
    if (!password) {
        return {
            valid: false,
            validation: {
                length: false,
                number: false,
                capitalLetter: false,
                lowercaseLetter: false,
                specialCharacter: false,
                personalInformation: false,
            },
        };
    }

    const validation = {
        length: password.length >= 8 && password.length <= 50,
        number: /\d/.test(password),
        capitalLetter: /[A-Z]/.test(password),
        lowercaseLetter: /[a-z]/.test(password),
        specialCharacter: hasSpecialCharacter(password),
        personalInformation: !containsPersonalInformation(password, personalTokens),
    };

    return {
        valid: Object.values(validation).every(Boolean),
        validation,
    };
};

const VALID_DASHBOARD_VIEWS = DASHBOARD_VIEW_OPTIONS.map((option) => option.value);

const buildPermissionsPayload = (permissions = {}) => {
    const payload = {};

    Object.keys(permissions).forEach((key) => {
        if (PERMISSION_META_KEYS.has(key)) {
            payload[key] = permissions[key];
            return;
        }
        payload[key] = Boolean(permissions[key]);
    });

    return payload;
};

const getLoginPermissionValidationError = (state, personalTokens = []) => {
    const userName = String(state.user.userName || '').trim();
    const password = String(state.user.password || '');
    const verifyPassword = String(state.user.verifyPassword || '');
    const initialPassword = String(state.initialPassword || '');
    const defaultLot = String(state.user.defaultLot || '').trim();
    const bhphDashboard = String(state.user.bhphDashboard || '').trim();
    const availableLotNames = state.availableLots
        .map((lot) => String(lot.lotName || '').trim())
        .filter(Boolean);
    const passwordChanged = password !== initialPassword
        || verifyPassword !== initialPassword;

    if (StringUtils.isEmpty(userName)) {
        return 'The Username is required please try again.';
    }
    if (userName.includes(' ')) {
        return 'The Username must not contain blank spaces.';
    }
    if (StringUtils.isEmpty(password)) {
        return 'The Password is required please try again.';
    }
    if (password.includes(' ')) {
        return 'The Password must not contain blank spaces.';
    }
    if (password !== verifyPassword) {
        return 'Passwords do not match.';
    }
    if (passwordChanged) {
        const { valid } = validateLoginPassword(password, personalTokens);
        if (!valid) {
            return 'The Password does not meet the requirements.';
        }
    }
    if (!VALID_DASHBOARD_VIEWS.includes(bhphDashboard)) {
        return 'Invalid BHPH Dashboard selection.';
    }
    if (defaultLot) {
        const matched = availableLotNames.some((lotName) => (
            lotName.toLowerCase() === defaultLot.toLowerCase()
        ));
        if (!matched) {
            return 'Default lot must be one of the available lots.';
        }
    }

    return null;
};

const buildLoginPermissionInput = (state) => {
    const userName = String(state.user.userName || '').trim();
    const password = String(state.user.password || '');
    const initialPassword = String(state.initialPassword || '');
    const defaultLot = String(state.user.defaultLot || '').trim();
    const availableLots = state.availableLots
        .map((lot) => String(lot.lotName || '').trim())
        .filter(Boolean);

    const userInput = {
        userName,
        isSupervisor: Boolean(state.user.isSupervisor),
        mustRenewPassword: Boolean(state.user.mustRenewPassword),
        bhphDashboard: String(state.user.bhphDashboard || 'Collector View').trim(),
        defaultLot: defaultLot || null,
    };

    if (password !== initialPassword) {
        userInput.password = password;
    }

    return {
        user: userInput,
        availableLots,
        permissions: buildPermissionsPayload(state.permissions),
    };
};

const MODULE_ICONS = {
    inventory: DirectionsCarOutlinedIcon,
    deals: DescriptionOutlinedIcon,
    dashboard: DashboardOutlinedIcon,
    desking: BuildOutlinedIcon,
    bhph: PaymentOutlinedIcon,
    crm: PeopleOutlinedIcon,
    hr: PersonOutlineOutlinedIcon,
    payroll: AttachMoneyOutlinedIcon,
    service: RoomServiceOutlinedIcon,
    parts: CategoryOutlinedIcon,
    accounting: AccountBalanceOutlinedIcon,
    settings: SettingsOutlinedIcon,
    reports: AssessmentOutlinedIcon,
    creditReporting: CreditCardOutlinedIcon,
    recon: BuildOutlinedIcon,
    bi: BarChartOutlinedIcon,
    websiteSettings: LanguageOutlinedIcon,
    electronicPayments: PaymentOutlinedIcon,
    training: SchoolOutlinedIcon,
};

const ACTION_TYPE = {
    HYDRATE: 'hydrate',
    SET_USER_FIELD: 'setUserField',
    TOGGLE_PERMISSION: 'togglePermission',
    SET_ALL_PERMISSIONS: 'setAllPermissions',
    TOGGLE_MODULE_NAV: 'toggleModuleNav',
    TOGGLE_CATEGORY: 'toggleCategory',
    SET_PERMISSION_FILTER: 'setPermissionFilter',
    SET_LOT_SEARCH: 'setLotSearch',
    TOGGLE_LOT_SELECTION: 'toggleLotSelection',
    MOVE_LOTS: 'moveLots',
};

const initState = {
    user: {
        userId: null,
        userName: '',
        password: '',
        verifyPassword: '',
        isSupervisor: false,
        mustRenewPassword: false,
        bhphDashboard: 'Collector View',
        defaultLot: '',
    },
    initialPassword: '',
    permissions: {},
    availableLots: [],
    hiddenLots: [],
    selectedHiddenLotIds: [],
    selectedAvailableLotIds: [],
    hiddenSearch: '',
    availableSearch: '',
    permissionFilter: '',
    collapsedCategories: {},
};

const normalizeLot = (lot) => ({
    lotId: Number(lot.lotId),
    lotName: lot.lotName || '',
});

const filterLots = (lots, search) => {
    const term = String(search || '').trim().toLowerCase();
    if (!term) return lots;
    return lots.filter((lot) => String(lot.lotName || '').toLowerCase().includes(term));
};

const detailReducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.HYDRATE: {
        const payload = action.payload || {};
        const availableLots = (payload.availableLots || []).map(normalizeLot);
        const rawDefaultLot = String(payload.user?.defaultLot || '').trim();
        const matchedDefaultLot = availableLots.find((lot) => (
            String(lot.lotName || '').toLowerCase() === rawDefaultLot.toLowerCase()
        ));
        const password = payload.user?.password || '';
        const user = {
            ...initState.user,
            ...(payload.user || {}),
            password,
            verifyPassword: password,
            defaultLot: matchedDefaultLot?.lotName || rawDefaultLot,
        };

        return update(state, {
            user: { $set: user },
            initialPassword: { $set: password },
            permissions: { $set: payload.permissions || {} },
            availableLots: { $set: availableLots },
            hiddenLots: { $set: (payload.hiddenLots || []).map(normalizeLot) },
            selectedHiddenLotIds: { $set: [] },
            selectedAvailableLotIds: { $set: [] },
            hiddenSearch: { $set: '' },
            availableSearch: { $set: '' },
            permissionFilter: { $set: '' },
        });
    }
    case ACTION_TYPE.SET_USER_FIELD:
        return update(state, {
            user: {
                [action.field]: { $set: action.value },
            },
        });
    case ACTION_TYPE.TOGGLE_PERMISSION:
        return update(state, {
            permissions: {
                [action.key]: { $set: !getPermissionValue(state.permissions, action.key) },
            },
        });
    case ACTION_TYPE.SET_ALL_PERMISSIONS: {
        const nextPermissions = { ...state.permissions };
        action.keys.forEach((key) => {
            nextPermissions[key] = action.value;
        });
        return update(state, {
            permissions: { $set: nextPermissions },
        });
    }
    case ACTION_TYPE.SET_LOT_SEARCH:
        return update(state, {
            [action.field]: { $set: action.value },
        });
    case ACTION_TYPE.TOGGLE_LOT_SELECTION: {
        const field = action.side === 'hidden'
            ? 'selectedHiddenLotIds'
            : 'selectedAvailableLotIds';
        const selected = state[field];
        const exists = selected.includes(action.lotId);
        return update(state, {
            [field]: {
                $set: exists
                    ? selected.filter((id) => id !== action.lotId)
                    : [...selected, action.lotId],
            },
        });
    }
    case ACTION_TYPE.MOVE_LOTS: {
        const {
            from, to, lotIds, moveAll,
        } = action;
        const fromKey = from === 'hidden' ? 'hiddenLots' : 'availableLots';
        const toKey = to === 'hidden' ? 'hiddenLots' : 'availableLots';
        const source = state[fromKey];
        const idsToMove = moveAll
            ? source.map((lot) => lot.lotId)
            : lotIds;
        if (idsToMove.length === 0) return state;

        const moving = source.filter((lot) => idsToMove.includes(lot.lotId));
        const remaining = source.filter((lot) => !idsToMove.includes(lot.lotId));
        const destination = [
            ...state[toKey],
            ...moving.filter((lot) => !state[toKey].some((item) => item.lotId === lot.lotId)),
        ].sort((a, b) => String(a.lotName).localeCompare(String(b.lotName)));

        return update(state, {
            [fromKey]: { $set: remaining },
            [toKey]: { $set: destination },
            selectedHiddenLotIds: { $set: [] },
            selectedAvailableLotIds: { $set: [] },
        });
    }
    case ACTION_TYPE.TOGGLE_MODULE_NAV: {
        if (!action.module.navKey) return state;
        return update(state, {
            permissions: {
                [action.module.navKey]: { $set: action.value },
            },
        });
    }
    case ACTION_TYPE.TOGGLE_CATEGORY:
        return update(state, {
            collapsedCategories: {
                [action.categoryId]: {
                    $set: !state.collapsedCategories[action.categoryId],
                },
            },
        });
    case ACTION_TYPE.SET_PERMISSION_FILTER:
        return update(state, {
            permissionFilter: { $set: action.value },
        });
    default:
        return state;
    }
};

const keyStore = new KeyStore();

const LoginAndPermissionDetail = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { userId: userIdParam } = useParams();
    const employeeId = Number(userIdParam);
    const employee = location.state?.employee || null;
    const canWrite = keyStore.hasPermission(Permission.HR_LOGIN_PERMISSION_WRITE);
    const [state, dispatch] = useReducer(detailReducer, initState);
    const [showPassword, setShowPassword] = useState(false);
    const [showVerifyPassword, setShowVerifyPassword] = useState(false);
    const [passwordTooltipOpen, setPasswordTooltipOpen] = useState(false);
    const [defaultLotDialogOpen, setDefaultLotDialogOpen] = useState(false);
    const [defaultLotDialogMessage, setDefaultLotDialogMessage] = useState('');

    const [loadDetail, { loading, error }] = useLazyQuery(
        EmployeeQuery.GET_EMPLOYEE_LOGIN_PERMISSION,
        {
            fetchPolicy: FetchPolicy.NETWORK_ONLY,
            onCompleted: (data) => {
                dispatch({
                    type: ACTION_TYPE.HYDRATE,
                    payload: data?.getEmployeeLoginPermission || {},
                });
            },
        },
    );

    const [saveLoginPermission, { loading: saving }] = useMutation(
        EmployeeMutation.UPDATE_EMPLOYEE_LOGIN_PERMISSION,
        {
            onCompleted: (data) => {
                if (data?.updateEmployeeLoginPermission) {
                    ModalUtils.successMessage(null, 'Log-In & Permissions saved successfully');
                    loadDetail({ variables: { userId: employeeId } });
                }
            },
            onError: (mutationError) => {
                ModalUtils.errorMessage(
                    mutationError?.graphQLErrors,
                    mutationError?.message,
                );
            },
        },
    );

    useEffect(() => {
        if (employeeId) {
            loadDetail({ variables: { userId: employeeId } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }
    }, [error]);

    const goBackToList = () => {
        history.push(`/${modules.HR}/${HR_SUBMODULES.LOGIN_AND_PERMISSION}`);
    };

    const isDefaultLot = (lot) => {
        const defaultLot = String(state.user.defaultLot || '').trim().toLowerCase();
        if (!defaultLot) return false;
        return String(lot?.lotName || '').trim().toLowerCase() === defaultLot;
    };

    const moveLotsToHidden = ({ lotIds = [], moveAll = false } = {}) => {
        const lotsToMove = moveAll
            ? state.availableLots
            : state.availableLots.filter((lot) => lotIds.includes(lot.lotId));

        if (lotsToMove.length === 0) return;

        const defaultLotBeingMoved = lotsToMove.find(isDefaultLot);
        if (defaultLotBeingMoved) {
            setDefaultLotDialogMessage(
                `"${defaultLotBeingMoved.lotName}" is currently the Default Lot. Please set another lot as Default before moving it to Hidden.`,
            );
            setDefaultLotDialogOpen(true);
            return;
        }

        dispatch({
            type: ACTION_TYPE.MOVE_LOTS,
            from: 'available',
            to: 'hidden',
            lotIds,
            moveAll,
        });
    };

    const closeDefaultLotDialog = () => {
        setDefaultLotDialogOpen(false);
        setDefaultLotDialogMessage('');
    };

    const onSaveChanges = () => {
        if (!canWrite || !employeeId || loading || saving) return;

        const personalTokens = getPersonalInfoTokens(employee, state.user.userName);
        const validationError = getLoginPermissionValidationError(state, personalTokens);
        if (validationError) {
            ModalUtils.errorMessage(null, validationError);
            return;
        }

        saveLoginPermission({
            variables: {
                userId: employeeId,
                input: buildLoginPermissionInput(state),
            },
        });
    };

    const permissionFilter = state.permissionFilter.trim().toLowerCase();
    const permissionModules = getPermissionModules()
        .map((module) => {
            const titleMatch = Boolean(permissionFilter)
                && module.title.toLowerCase().includes(permissionFilter);
            return {
                ...module,
                permissions: titleMatch
                    ? module.permissions
                    : module.permissions.filter((item) => {
                        if (!permissionFilter) return true;
                        return item.label.toLowerCase().includes(permissionFilter)
                            || item.key.toLowerCase().includes(permissionFilter);
                    }),
            };
        })
        .filter((module) => {
            if (!permissionFilter) return true;
            if (module.title.toLowerCase().includes(permissionFilter)) return true;
            return module.permissions.length > 0;
        });
    const allPermissionKeys = getAllCatalogKeys();
    const allChecked = allPermissionKeys.length > 0
        && allPermissionKeys.every((key) => getPermissionValue(state.permissions, key));

    const defaultLotOptions = (() => {
        const options = state.availableLots.map((lot) => ({
            value: lot.lotName,
            label: lot.lotName,
        }));
        const currentDefault = String(state.user.defaultLot || '').trim();
        if (
            currentDefault
            && !options.some((option) => option.value === currentDefault)
        ) {
            options.unshift({
                value: currentDefault,
                label: currentDefault,
            });
        }
        return options;
    })();

    const filteredHiddenLots = filterLots(state.hiddenLots, state.hiddenSearch);
    const filteredAvailableLots = filterLots(state.availableLots, state.availableSearch);

    const personalTokens = getPersonalInfoTokens(employee, state.user.userName);
    const { validation: passwordValidation } = validateLoginPassword(
        state.user.password || '',
        personalTokens,
    );

    const renderPasswordVisibilityToggle = (visible, onToggle) => (
        <InputGroup.Append>
            <InputGroup.Text className={classes.inputIcon}>
                <button
                    type="button"
                    className={classes.visibilityButton}
                    aria-label={visible ? 'Hide password' : 'Show password'}
                    onClick={onToggle}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
            </InputGroup.Text>
        </InputGroup.Append>
    );

    const passwordRequirementsBody = (
        <div className={classes.passwordRules}>
            {PASSWORD_RULES.map((rule) => {
                const passed = Boolean(passwordValidation[rule.key]);
                return (
                    <div key={rule.key} className={classes.passwordRule}>
                        {passed ? (
                            <CheckCircleIcon className={classes.passwordRuleIconPass} />
                        ) : (
                            <CancelIcon className={classes.passwordRuleIconFail} />
                        )}
                        <span>{rule.label}</span>
                    </div>
                );
            })}
        </div>
    );

    const renderLotList = (lots, side, selectedIds, searchField) => {
        const isAssigned = side === 'available';
        return (
            <div className={clsx(classes.lotPanel, isAssigned && classes.lotPanelAssigned)}>
                <div className={clsx(classes.lotPanelTitle, isAssigned && classes.lotPanelTitleAssigned)}>
                    {isAssigned
                        ? <StoreOutlinedIcon />
                        : <VisibilityOffOutlinedIcon />}
                    {isAssigned ? 'Assigned / Available Lots' : 'Hidden Lots'}
                </div>
                <div className={classes.lotPanelHeader}>
                    <Form.Control
                        size="sm"
                        className={clsx(classes.lotSearch, classes.smallField)}
                        placeholder={isAssigned ? 'Search assigned...' : 'Search hidden...'}
                        value={state[searchField]}
                        onChange={(event) => dispatch({
                            type: ACTION_TYPE.SET_LOT_SEARCH,
                            field: searchField,
                            value: event.target.value,
                        })}
                    />
                </div>
                <div className={classes.lotList}>
                    {lots.map((lot) => {
                        const isSelected = selectedIds.includes(lot.lotId);
                        const isDefault = isAssigned
                            && state.user.defaultLot
                            && String(state.user.defaultLot).toLowerCase()
                                === String(lot.lotName).toLowerCase();
                        return (
                            <div
                                key={lot.lotId}
                                className={clsx(
                                    classes.lotItem,
                                    isSelected && classes.lotItemSelected,
                                )}
                            >
                                <Form.Check
                                    type="checkbox"
                                    id={`lot-${side}-${lot.lotId}`}
                                    className={classes.lotCheckbox}
                                    checked={isSelected}
                                    onChange={() => dispatch({
                                        type: ACTION_TYPE.TOGGLE_LOT_SELECTION,
                                        side,
                                        lotId: lot.lotId,
                                    })}
                                />
                                <Typography variant="body2">{lot.lotName}</Typography>
                                {isDefault
                                    ? <span className={classes.defaultBadge}>DEFAULT</span>
                                    : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTransferDoubleIcon = (direction) => (
        <span className={classes.transferDoubleIcon}>
            {direction === 'right' ? (
                <>
                    <KeyboardArrowRightIcon />
                    <KeyboardArrowRightIcon />
                </>
            ) : (
                <>
                    <KeyboardArrowLeftIcon />
                    <KeyboardArrowLeftIcon />
                </>
            )}
        </span>
    );

    return (
        <div className={classes.root}>
            <div className={classes.topBar}>
                <div className={classes.userInfo}>
                    <Avatar
                        className={classes.avatar}
                        src={state.user.userPicture || employee?.userPicture || avatarUser}
                        alt={employee?.fullName || state.user.userName}
                    />
                    <div className={classes.userText}>
                        <Typography className={classes.userName}>
                            {employee?.fullName || state.user.userName || 'Employee'}
                        </Typography>
                        <Typography className={classes.userMeta}>
                            {`${employee?.employeeType || 'Employee'} • ID: ${employee?.userId || state.user.userId || employeeId || ''}`}
                        </Typography>
                    </div>
                </div>
                <div className={classes.actions}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={goBackToList}
                    >
                        Close
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        className={classes.containedInfo}
                        startIcon={<SaveIcon />}
                        disabled={!canWrite || loading || saving || !employeeId}
                        onClick={onSaveChanges}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
            <div className={classes.content}>
                {loading ? (
                    <div className={classes.loadingBox}>
                        <Loading />
                    </div>
                ) : (
                    <>
                        <div className={classes.topGrid}>
                            <div className={classes.card}>
                                <div className={classes.cardTitle}>
                                    <VpnKeyOutlinedIcon />
                                    Login Credentials & Defaults
                                </div>
                                <div className={classes.fieldGroup}>
                                    <Typography className={classes.fieldLabel}>User Name</Typography>
                                    <InputGroup size="sm" className={clsx(classes.input, classes.smallField)}>
                                        <Form.Control
                                            size="sm"
                                            value={state.user.userName || ''}
                                            onChange={(event) => dispatch({
                                                type: ACTION_TYPE.SET_USER_FIELD,
                                                field: 'userName',
                                                value: event.target.value,
                                            })}
                                        />
                                        <InputGroup.Append>
                                            <InputGroup.Text className={classes.inputIcon}>
                                                <PersonOutlineOutlinedIcon fontSize="small" />
                                            </InputGroup.Text>
                                        </InputGroup.Append>
                                    </InputGroup>
                                </div>
                                <div className={clsx(classes.row, classes.fieldGroup)}>
                                    <div>
                                        <Typography className={classes.fieldLabel}>Password</Typography>
                                        <FormattedTooltip
                                            header="Password Requirements:"
                                            body={passwordRequirementsBody}
                                            placement="right"
                                            open={passwordTooltipOpen}
                                            disableFocusListener
                                            disableHoverListener
                                            disableTouchListener
                                            interactive
                                            classes={{ tooltip: classes.passwordTooltip }}
                                        >
                                            <div>
                                                <InputGroup size="sm" className={clsx(classes.passwordInput, classes.smallField)}>
                                                    <Form.Control
                                                        size="sm"
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={state.user.password || ''}
                                                        autoComplete="new-password"
                                                        maxLength={50}
                                                        onFocus={() => setPasswordTooltipOpen(true)}
                                                        onBlur={() => setPasswordTooltipOpen(false)}
                                                        onChange={(event) => dispatch({
                                                            type: ACTION_TYPE.SET_USER_FIELD,
                                                            field: 'password',
                                                            value: event.target.value,
                                                        })}
                                                    />
                                                    {renderPasswordVisibilityToggle(
                                                        showPassword,
                                                        () => setShowPassword((prev) => !prev),
                                                    )}
                                                </InputGroup>
                                            </div>
                                        </FormattedTooltip>
                                    </div>
                                    <div>
                                        <Typography className={classes.fieldLabel}>Verify Password</Typography>
                                        <InputGroup size="sm" className={clsx(classes.passwordInput, classes.smallField)}>
                                            <Form.Control
                                                size="sm"
                                                type={showVerifyPassword ? 'text' : 'password'}
                                                value={state.user.verifyPassword || ''}
                                                autoComplete="new-password"
                                                maxLength={50}
                                                isInvalid={Boolean(
                                                    state.user.verifyPassword
                                                    && state.user.verifyPassword !== state.user.password,
                                                )}
                                                aria-invalid={Boolean(
                                                    state.user.verifyPassword
                                                    && state.user.verifyPassword !== state.user.password,
                                                )}
                                                onChange={(event) => dispatch({
                                                    type: ACTION_TYPE.SET_USER_FIELD,
                                                    field: 'verifyPassword',
                                                    value: event.target.value,
                                                })}
                                            />
                                            {renderPasswordVisibilityToggle(
                                                showVerifyPassword,
                                                () => setShowVerifyPassword((prev) => !prev),
                                            )}
                                        </InputGroup>
                                        {state.user.verifyPassword
                                            && state.user.verifyPassword !== state.user.password ? (
                                                <div className={classes.fieldError}>Passwords do not match</div>
                                            ) : null}
                                    </div>
                                </div>
                                <div className={classes.checksRow}>
                                    <Form.Check
                                        type="checkbox"
                                        id="login-is-supervisor"
                                        className={classes.checkItem}
                                        label="Administrator"
                                        checked={Boolean(state.user.isSupervisor)}
                                        onChange={(event) => dispatch({
                                            type: ACTION_TYPE.SET_USER_FIELD,
                                            field: 'isSupervisor',
                                            value: event.target.checked,
                                        })}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="login-must-renew-password"
                                        className={classes.checkItem}
                                        label="Must Renew Password"
                                        checked={Boolean(state.user.mustRenewPassword)}
                                        onChange={(event) => dispatch({
                                            type: ACTION_TYPE.SET_USER_FIELD,
                                            field: 'mustRenewPassword',
                                            value: event.target.checked,
                                        })}
                                    />
                                </div>
                                <div className={classes.row}>
                                    <div>
                                        <Typography className={classes.fieldLabel}>Default Lot</Typography>
                                        <Select
                                            name="defaultLot"
                                            className={classes.input}
                                            options={defaultLotOptions}
                                            value={state.user.defaultLot || ''}
                                            onChange={(_, value) => dispatch({
                                                type: ACTION_TYPE.SET_USER_FIELD,
                                                field: 'defaultLot',
                                                value,
                                            })}
                                            size="sm"
                                        />
                                    </div>
                                    <div>
                                        <Typography className={classes.fieldLabel}>Dashboard View</Typography>
                                        <Select
                                            name="bhphDashboard"
                                            className={classes.input}
                                            options={DASHBOARD_VIEW_OPTIONS}
                                            value={state.user.bhphDashboard || 'Collector View'}
                                            onChange={(_, value) => dispatch({
                                                type: ACTION_TYPE.SET_USER_FIELD,
                                                field: 'bhphDashboard',
                                                value,
                                            })}
                                            size="sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={classes.card}>
                                <div className={classes.cardTitle}>
                                    <BusinessOutlinedIcon />
                                    Dealership Lot Access & Visibility
                                </div>
                                <div className={classes.lotsLayout}>
                                    {renderLotList(
                                        filteredHiddenLots,
                                        'hidden',
                                        state.selectedHiddenLotIds,
                                        'hiddenSearch',
                                    )}
                                    <div className={classes.lotTransfer}>
                                        <IconButton
                                            className={clsx(
                                                classes.transferButton,
                                                classes.transferButtonPrimary,
                                            )}
                                            size="small"
                                            aria-label="Move all hidden lots to available"
                                            onClick={() => dispatch({
                                                type: ACTION_TYPE.MOVE_LOTS,
                                                from: 'hidden',
                                                to: 'available',
                                                moveAll: true,
                                            })}
                                        >
                                            {renderTransferDoubleIcon('right')}
                                        </IconButton>
                                        <IconButton
                                            className={clsx(
                                                classes.transferButton,
                                                classes.transferButtonPrimary,
                                            )}
                                            size="small"
                                            aria-label="Move selected hidden lots to available"
                                            onClick={() => dispatch({
                                                type: ACTION_TYPE.MOVE_LOTS,
                                                from: 'hidden',
                                                to: 'available',
                                                lotIds: state.selectedHiddenLotIds,
                                            })}
                                        >
                                            <KeyboardArrowRightIcon />
                                        </IconButton>
                                        <IconButton
                                            className={classes.transferButton}
                                            size="small"
                                            aria-label="Move selected available lots to hidden"
                                            onClick={() => moveLotsToHidden({
                                                lotIds: state.selectedAvailableLotIds,
                                            })}
                                        >
                                            <KeyboardArrowLeftIcon />
                                        </IconButton>
                                        <IconButton
                                            className={classes.transferButton}
                                            size="small"
                                            aria-label="Move all available lots to hidden"
                                            onClick={() => moveLotsToHidden({ moveAll: true })}
                                        >
                                            {renderTransferDoubleIcon('left')}
                                        </IconButton>
                                    </div>
                                    {renderLotList(
                                        filteredAvailableLots,
                                        'available',
                                        state.selectedAvailableLotIds,
                                        'availableSearch',
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={classes.permissionsCard}>
                            <div className={classes.permissionsHeader}>
                                <div className={classes.permissionsTitle}>
                                    <SecurityOutlinedIcon fontSize="small" />
                                    Granular Permissions
                                </div>
                                <div className={classes.permissionsTools}>
                                    <InputGroup size="sm" className={clsx(classes.permissionFilter, classes.smallField)}>
                                        <InputGroup.Prepend>
                                            <InputGroup.Text>
                                                <SearchIcon />
                                            </InputGroup.Text>
                                        </InputGroup.Prepend>
                                        <Form.Control
                                            size="sm"
                                            placeholder="Filter permissions..."
                                            value={state.permissionFilter}
                                            onChange={(event) => dispatch({
                                                type: ACTION_TYPE.SET_PERMISSION_FILTER,
                                                value: event.target.value,
                                            })}
                                        />
                                    </InputGroup>
                                    <Form.Check
                                        type="checkbox"
                                        id="permissions-check-all"
                                        className={classes.checkAll}
                                        label="Check All"
                                        checked={allChecked}
                                        onChange={(event) => dispatch({
                                            type: ACTION_TYPE.SET_ALL_PERMISSIONS,
                                            keys: allPermissionKeys,
                                            value: event.target.checked,
                                        })}
                                    />
                                </div>
                            </div>
                            <div className={classes.permissionsBody}>
                                {permissionModules.map((module) => {
                                    const ModuleIcon = MODULE_ICONS[module.id] || SecurityOutlinedIcon;
                                    const hasItems = module.permissions.length > 0;
                                    const collapsed = Boolean(state.collapsedCategories[module.id]);
                                    const showNavCheckbox = Boolean(module.navKey)
                                        && module.showNavCheckbox !== false;
                                    const navChecked = showNavCheckbox
                                        && getPermissionValue(state.permissions, module.navKey);

                                    return (
                                        <div key={module.id} className={classes.category}>
                                            {hasItems ? (
                                                <div
                                                    className={classes.categoryHeader}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => dispatch({
                                                        type: ACTION_TYPE.TOGGLE_CATEGORY,
                                                        categoryId: module.id,
                                                    })}
                                                    onKeyDown={(event) => {
                                                        if (event.target !== event.currentTarget) {
                                                            return;
                                                        }
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            dispatch({
                                                                type: ACTION_TYPE.TOGGLE_CATEGORY,
                                                                categoryId: module.id,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <div className={classes.categoryHeaderLeft}>
                                                        {showNavCheckbox ? (
                                                            <div
                                                                className={classes.categoryCheckWrap}
                                                                onClick={(event) => event.stopPropagation()}
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    id={`nav-${module.id}`}
                                                                    className={clsx(classes.permissionItem, classes.categoryCheck)}
                                                                    checked={navChecked}
                                                                    onChange={(event) => dispatch({
                                                                        type: ACTION_TYPE.TOGGLE_MODULE_NAV,
                                                                        module,
                                                                        value: event.target.checked,
                                                                    })}
                                                                    label={(
                                                                        <span className={classes.categoryHeaderLabel}>
                                                                            <ModuleIcon className={classes.categoryIcon} />
                                                                            <Typography className={classes.categoryTitle} component="span">
                                                                                {module.title}
                                                                            </Typography>
                                                                        </span>
                                                                    )}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className={classes.categoryTitleOnly}>
                                                                <ModuleIcon className={classes.categoryIcon} />
                                                                <Typography className={classes.categoryTitle}>
                                                                    {module.title}
                                                                </Typography>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {collapsed
                                                        ? <ExpandMoreIcon color="action" />
                                                        : <ExpandLessIcon color="action" />}
                                                </div>
                                            ) : (
                                                <div
                                                    className={classes.categoryHeader}
                                                    style={{ cursor: 'default' }}
                                                >
                                                    <div className={classes.categoryHeaderLeft}>
                                                        {showNavCheckbox ? (
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`nav-static-${module.id}`}
                                                                className={clsx(classes.permissionItem, classes.categoryCheck)}
                                                                checked={navChecked}
                                                                onChange={(event) => dispatch({
                                                                    type: ACTION_TYPE.TOGGLE_MODULE_NAV,
                                                                    module,
                                                                    value: event.target.checked,
                                                                })}
                                                                label={(
                                                                    <span className={classes.categoryHeaderLabel}>
                                                                        <ModuleIcon className={classes.categoryIcon} />
                                                                        <Typography className={classes.categoryTitle} component="span">
                                                                            {module.title}
                                                                        </Typography>
                                                                    </span>
                                                                )}
                                                            />
                                                        ) : (
                                                            <div className={classes.categoryTitleOnly}>
                                                                <ModuleIcon className={classes.categoryIcon} />
                                                                <Typography className={classes.categoryTitle}>
                                                                    {module.title}
                                                                </Typography>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {hasItems && !collapsed && (
                                                <div className={classes.categoryBody}>
                                                    {module.permissions.map((item) => (
                                                        <Form.Check
                                                            key={item.key}
                                                            type="checkbox"
                                                            id={`permission-${item.key}`}
                                                            className={classes.permissionItem}
                                                            label={item.label}
                                                            checked={getPermissionValue(
                                                                state.permissions,
                                                                item.key,
                                                            )}
                                                            onChange={() => dispatch({
                                                                type: ACTION_TYPE.TOGGLE_PERMISSION,
                                                                key: item.key,
                                                            })}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ConfirmDialog
                open={defaultLotDialogOpen}
                title="Default Lot Required"
                description={defaultLotDialogMessage}
                titlePrimary="Ok"
                showHeader
                hiddenSecondaryButton
                onClose={closeDefaultLotDialog}
                onClickPrimary={closeDefaultLotDialog}
            />
        </div>
    );
};

export default LoginAndPermissionDetail;
